import React, {useRef, useState} from 'react';

import cs from 'classnames';
import {useDispatch} from 'react-redux';
import {browserHistory} from 'react-router';
import {
  ButtonGroup,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';

import {useRequestVersionAcceptance} from 'app/acceptance/api';
import {AttachOverlay} from 'app/general/overlays';
import StateButton from 'app/general/widgets/StateButton';
import {useApprovePolicyVersion} from 'app/policies/api';
import {useApproveProcedureVersion} from 'app/procedures/api';
import {useApproveProcessVersion} from 'app/process/api';
import {useApproveQuizVersion} from 'app/quizzes/api';
import {showNotification} from 'app/toasts/slice';
import {gt} from 'app/utils/i18n';
import {getLocation} from 'app/utils/urls';

import {RequestApprovalPopover} from './RequestApprovalButton/RequestApprovalPopover';

import type {
  BaseDocumentVersionContentType,
  BasicUserSerializedType,
  DocumentContentType,
} from 'sweet-types';

type Props = {
  contentType: DocumentContentType;
  documentId: number | string;
  documentHtmlURL: string;
  versionURL: string;
  onApprove?: (approvalFunction: () => Promise<unknown>) => Promise<unknown>;
  disabled: boolean;
  versionApprovedAt: string | null;
  versionId?: number | string;
  onApprovalRequest?: (
    requestApproveFunction: () => Promise<unknown>
  ) => Promise<unknown>;
  managers: Array<BasicUserSerializedType>;
  className?: string;
  groupClass?: string;
  buttonClass?: string;
  color?: string;
  size?: string;
  isDropDown?: boolean;
  nextReviewDate: string | null;
  refreshData?: () => unknown;
};

export function ApproveButton({
  onApprove,
  contentType,
  documentId,
  versionURL,
  groupClass,
  documentHtmlURL,
  versionApprovedAt,
  nextReviewDate,
  disabled,
  managers,
  versionId,
  onApprovalRequest,
  buttonClass,
  refreshData,
  className,
  color,
  isDropDown = true,
  size,
}: Props): JSX.Element {
  const dispatch = useDispatch();

  const [approvePolicyVersion] = useApprovePolicyVersion();
  const [approveProcessVersion] = useApproveProcessVersion();
  const [approveProcedureVersion] = useApproveProcedureVersion();
  const [requestVersionAcceptance] = useRequestVersionAcceptance();
  const [approveQuizVersion] = useApproveQuizVersion();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const requestApprovalPopoverNode = useRef<HTMLElement | null>(null);

  const approve = ({
    notify,
    signoff,
  }: {
    notify?: boolean;
    signoff?: boolean;
  }): Promise<void> => {
    const approveFunc = {
      procedure: approveProcedureVersion,
      policy: approvePolicyVersion,
      proceduregroup: approveProcessVersion,
      quiz: approveQuizVersion,
    }[contentType];

    const approveCall = async () => {
      const result = await approveFunc({
        versionURL,
        // @ts-expect-error we don't really care that documentId is string|number here.
        documentId,
        options: {notify, signoff},
      });
      if (nextReviewDate != null) {
        dispatch(showNotification(gt('You have also reviewed this document.')));
      }
      return result;
    };

    return (onApprove != null ? onApprove(approveCall) : approveCall())
      .then(() => browserHistory.push(getLocation(documentHtmlURL)))
      .catch(() => {
        setIsOpen(true);
      });
  };

  const requestConfirmation = (message: string, recipients: Array<string>) => {
    if (versionId == null) {
      setIsOpen(false);
      return Promise.reject();
    }

    const versionContentType: BaseDocumentVersionContentType = {
      procedure: 'procedureversion' as const,
      proceduregroup: 'processversion' as const,
      policy: 'policyversion' as const,
    }[contentType];

    const requestCall = () =>
      requestVersionAcceptance({
        message,
        versionId,
        user_ids: recipients,
        versionContentType,
      }).unwrap();

    return onApprovalRequest != null
      ? onApprovalRequest(requestCall).then(() => setIsOpen(true))
      : requestCall().then(() => setIsOpen(false));
  };

  const toggle = (e: React.MouseEvent<void>) => {
    const popoverNode = requestApprovalPopoverNode.current;

    const toggle = () => setIsOpen(!isOpen);

    // we don't want to close dropdown menu while the user clicks on the
    // request-approval popover elements
    // so here we do some checks to only close the menu when user clicks
    // outside the popover
    if (
      // its a click to open the popover - no need to check anything
      !isOpen || // popoverNode was not captured yet - probably its a click to open it
      popoverNode == null || // make sure the clicked element is a Node (Flow wants that)
      // and it lives outside the popover
      (e.target instanceof Node && !popoverNode.contains(e.target))
    ) {
      toggle();
    }
  };

  return (
    <ButtonGroup className={cs('request-or-approve-tour', groupClass)}>
      <StateButton
        className={
          className != null
            ? className
            : buttonClass != null
            ? buttonClass
            : 'btn btn-primary'
        }
        disabled={disabled}
        busyText={
          <span className="d-none d-sm-inline">{gt('Approving...')}</span>
        }
        onClick={() => approve({})}
      >
        <>
          <i className="fa fa-check" />
          <span className="d-none d-sm-inline">
            {' '}
            {versionApprovedAt == null ? gt('Approve') : gt('Mark as live')}
          </span>
        </>
      </StateButton>
      {isDropDown && (
        <Dropdown group isOpen={isOpen} toggle={toggle}>
          <DropdownToggle
            color={color || 'primary'}
            size={size}
            disabled={disabled}
            caret
            className={className}
          />
          <DropdownMenu right>
            <DropdownItem onClick={() => approve({notify: true})}>
              <i className="far fa-envelope" />{' '}
              {versionApprovedAt == null
                ? gt('Approve & Notify')
                : gt('Mark as live & Notify')}
            </DropdownItem>
            <DropdownItem onClick={() => approve({signoff: true})}>
              <i className="fa fa-signature" />{' '}
              {versionApprovedAt == null
                ? gt('Approve & Request Sign Off')
                : gt('Mark as live & Request Sign Off')}
            </DropdownItem>
            {versionApprovedAt == null && versionId != null ? (
              <AttachOverlay
                trigger="click"
                overlay={({overlayProps}) => (
                  <RequestApprovalPopover
                    // eslint-disable-next-line
                    // @ts-ignore
                    ref={requestApprovalPopoverNode}
                    overlayProps={{...overlayProps, placement: 'bottom'}}
                    managers={managers}
                    contentType={contentType}
                    dontCloseOnError={true}
                    onSubmit={requestConfirmation}
                    refreshData={refreshData}
                  />
                )}
              >
                {(handlers) => (
                  <button type="button" className="dropdown-item" {...handlers}>
                    <i className="far fa-thumbs-up" /> {gt('Request Approval')}
                  </button>
                )}
              </AttachOverlay>
            ) : null}
          </DropdownMenu>
        </Dropdown>
      )}
    </ButtonGroup>
  );
}
